/**
 * SourceHealthDashboard.tsx
 *
 * Worker status bar + searchable coverage table.
 */
import React, { useEffect, useState } from 'react';
import { useSupabaseAuth } from '@/lib/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  RefreshCw,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Cpu,
  Server,
} from 'lucide-react';
import { useSourceHealth } from '@/hooks/useSourceHealth';
import { apiFetch } from '@/lib/apiFetch';
import { CoverageTable } from '@/components/admin/CoverageTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hrs = Math.floor(mins / 60);
  if (diff < 60_000) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const SourceHealthDashboard: React.FC = () => {
  const { isLoaded: authLoaded } = useSupabaseAuth();
  const { sources, worker, loading, error, lastRefreshed, refresh } =
    useSourceHealth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshingCache, setRefreshingCache] = useState(false);

  // Wait for auth to restore the session from localStorage before fetching.
  // Without this guard, refresh() fires while session=null → 401.
  useEffect(() => {
    if (!authLoaded) return;
    void refresh();
  }, [authLoaded, refresh]);

  // Rebuild the coverage cache server-side, then re-fetch everything.
  const handleRefresh = async () => {
    setRefreshingCache(true);
    try {
      await apiFetch('/api/source-health/coverage/refresh', {
        method: 'POST',
      });
    } catch {
      /* cache refresh is best-effort; the fetch below still runs */
    } finally {
      setRefreshingCache(false);
      void refresh();
      setRefreshKey((k) => k + 1);
    }
  };

  const workerOnline = worker?.status === 'online';
  const workerUnknown = worker === null;

  return (
    <div className="space-y-4">
      {/* ── Top bar: worker status + refresh ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-bold text-gray-900 tracking-tight">
            Source Health
          </h2>

          {/* Separator */}
          <span className="text-gray-300 hidden sm:inline">|</span>

          {/* Worker liveness badge */}
          {loading && !worker ? (
            <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-300 rounded-full px-3 py-1">
              <Loader2 className="w-3.5 h-3.5 text-gray-500 animate-spin" />
              <span className="text-xs text-gray-600 font-medium">
                Checking worker…
              </span>
            </div>
          ) : workerUnknown ? (
            <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-300 rounded-full px-3 py-1">
              <Server className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs text-gray-600 font-medium">
                Worker unknown
              </span>
            </div>
          ) : workerOnline ? (
            <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 rounded-full px-3 py-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-800">
                Worker Online
              </span>
              {worker.jobs_active > 0 && (
                <span className="text-xs text-emerald-700 font-medium">
                  · {worker.jobs_active} job
                  {worker.jobs_active !== 1 ? 's' : ''} active
                </span>
              )}
              <span className="text-xs text-emerald-600">
                · {fmtAgo(worker.last_seen)}
              </span>
              {worker.pid && (
                <span className="text-xs text-emerald-600 font-mono">
                  · PID {worker.pid}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 bg-red-50 border border-red-300 rounded-full px-3 py-1">
              <XCircle className="w-3.5 h-3.5 text-red-600" />
              <span className="text-xs font-semibold text-red-800">
                Worker Offline
              </span>
              <span className="text-xs text-red-600">
                · last seen {fmtAgo(worker?.last_seen ?? null)}
              </span>
            </div>
          )}

          {workerOnline && worker && worker.jobs_active > 0 && (
            <div className="flex items-center gap-1 text-xs text-blue-700 font-medium">
              <Cpu className="w-3.5 h-3.5 animate-pulse" />
              Processing
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-gray-400">
              Refreshed {fmtAgo(lastRefreshed.toISOString())}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleRefresh()}
            disabled={loading || refreshingCache}
            className="border-gray-300 text-gray-700 bg-white hover:bg-gray-50 font-medium gap-1"
          >
            {loading || refreshingCache ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {refreshingCache ? 'Rebuilding…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* ── Error state ── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-300 text-red-800 rounded-xl p-4 text-sm font-medium">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-red-600" />
          {error}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-red-700 hover:text-red-900 hover:bg-red-100"
            onClick={() => void refresh()}
          >
            Retry
          </Button>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && sources.length === 0 && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-32 bg-gray-100 rounded-xl animate-pulse border border-gray-200"
            />
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && !error && sources.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <Activity className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-gray-500 font-medium">No sources found.</p>
        </div>
      )}

      {/* ── Coverage Table ── */}
      <CoverageTable refreshKey={refreshKey} />
    </div>
  );
};
